// src/PricingPage.jsx
// Pricing page with 3 plans → Stripe Checkout
import React, { useEffect, useRef, useState } from 'react';
import { useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { Check, Sparkles, Zap, ArrowRight, X, Loader } from 'lucide-react';
import { trackEvent } from './analytics';

const PLANS = [
  {
    id: 'onetime',
    name: '7-Day Pass',
    price: '€4.99',
    period: 'one payment',
    description: 'A focused week to prepare your applications',
    duration: 'One payment · access ends after 7 days · no renewal',
    cta: 'Get One-Time Access',
    highlight: true,
    badge: 'NO RECURRING BILLING',
    features: [
      'All 15 templates',
      'All 10 colours',
      '20 AI writing tools per day',
      'No watermark',
      'Additional CV imports',
      'Save CV versions in this browser'
    ]
  },
  {
    id: 'monthly',
    name: 'Pro Monthly',
    price: '€6.99',
    period: '/month',
    description: 'Recurring monthly billing · cancel anytime',
    cta: 'Start Monthly',
    features: [
      'Everything in One-Time',
      '30 AI uses per day',
      'Multiple CV versions in this browser',
      'Priority support'
    ]
  },
  {
    id: 'annual',
    name: 'Pro Annual',
    price: '€34.99',
    period: '/year',
    badge: 'BEST VALUE',
    sub: '€2.92/month — Save 58%',
    description: 'Billed yearly · renews annually until canceled',
    cta: 'Start Annual',
    features: [
      'Everything in Monthly',
      '50 AI uses per day',
      '12 months full access',
      '58% cheaper than monthly',
      'Best for active job hunts'
    ],
    highlight: false
  }
];

export default function PricingPage({ onClose }) {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { openSignIn } = useClerk();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [error, setError] = useState('');
  const checkoutStarted = useRef(false);

  useEffect(() => { trackEvent('pricing_viewed'); }, []);

  const startCheckout = async (planId) => {
    if (checkoutStarted.current) return;
    checkoutStarted.current = true;
    setLoadingPlan(planId);
    setError('');
    trackEvent('checkout_started', { plan: planId });
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getToken()}` },
        body: JSON.stringify({ plan: planId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Checkout failed');
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
      setLoadingPlan(null);
      checkoutStarted.current = false;
    }
  };

  useEffect(() => {
    if (!isSignedIn || !pendingPlan || checkoutStarted.current) return;
    const planId = pendingPlan;
    setPendingPlan(null);
    startCheckout(planId);
  }, [isSignedIn, pendingPlan]);

  const handleSubscribe = async (planId) => {
    if (loadingPlan) return;
    setError('');
    if (isSignedIn) {
      await startCheckout(planId);
      return;
    }

    setPendingPlan(planId);
    try {
      await openSignIn();
    } catch (e) {
      setPendingPlan(null);
      setError(e.message || 'Unable to open sign in. Please try again.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f9fafb 0%, #eff6ff 100%)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            ← Back
          </button>
          {isSignedIn && (
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              Signed in as {user?.primaryEmailAddress?.emailAddress}
            </span>
          )}
        </div>

        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', padding: '6px 12px', borderRadius: 999, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 14, fontSize: 12, fontWeight: 600, color: '#4f46e5' }}>
            <Sparkles size={14}/> Upgrade to Pro
          </div>
          <h1 style={{ margin: 0, fontSize: 36, fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>
            Choose the access that suits your job search
          </h1>
          <p style={{ margin: '12px auto 0', maxWidth: 540, fontSize: 16, color: '#4b5563', lineHeight: 1.5 }}>
            The CV builder, PDF export, and one AI-powered CV import per day are free after sign-in. Pro unlocks all 15 templates, more AI tools, and watermark-free PDFs.
          </p>
        </div>

        {error && (
          <div style={{ maxWidth: 540, margin: '0 auto 20px', padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 10, textAlign: 'center', fontSize: 14, border: '1px solid #fecaca' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Plans grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 40 }}>
          {PLANS.map(plan => (
            <div key={plan.id} style={{
              background: '#fff',
              borderRadius: 16,
              padding: 28,
              border: plan.highlight ? '2px solid #4f46e5' : '1px solid #e5e7eb',
              boxShadow: plan.highlight ? '0 12px 40px rgba(79, 70, 229, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
              position: 'relative',
              transform: plan.highlight ? 'scale(1.02)' : 'none'
            }}>
              {plan.badge && (
                <div style={{ position: 'absolute', top: -10, right: 20, background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: '#fff', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
                  {plan.badge}
                </div>
              )}
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>{plan.name}</h3>
              <p style={{ margin: '4px 0 16px', fontSize: 13, color: '#6b7280' }}>{plan.description}</p>
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 40, fontWeight: 800, color: '#111827' }}>{plan.price}</span>
                <span style={{ fontSize: 15, color: '#6b7280', marginLeft: 4 }}>{plan.period}</span>
              </div>
              {plan.sub && <p style={{ margin: '0 0 4px', fontSize: 12, color: '#4f46e5', fontWeight: 600 }}>{plan.sub}</p>}
              {plan.duration && <p style={{ margin: '0 0 4px', fontSize: 12, color: '#6b7280' }}>{plan.duration}</p>}
              {plan.id === 'monthly' && <p style={{ margin: '0 0 4px', fontSize: 12, color: '#6b7280' }}>Renews every month until canceled.</p>}
              {plan.id === 'annual' && <p style={{ margin: '0 0 4px', fontSize: 12, color: '#6b7280' }}>One annual charge of €34.99; renews yearly until canceled.</p>}

              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loadingPlan === plan.id}
                style={{
                  width: '100%',
                  marginTop: 20,
                  marginBottom: 20,
                  padding: '12px 16px',
                  background: plan.highlight ? 'linear-gradient(135deg, #4f46e5, #7c3aed)' : '#111827',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: loadingPlan === plan.id ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6
                }}>
                {loadingPlan === plan.id ? <><Loader size={14} style={{ animation: 'spin 0.8s linear infinite' }}/> Loading...</> : <>{plan.cta} <ArrowRight size={14}/></>}
              </button>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {plan.features.map((f, i) => (
                  <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 13, color: '#374151', alignItems: 'flex-start' }}>
                    <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }}/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div style={{ maxWidth: 760, margin: '32px auto 0', padding: 20, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 16, color: '#111827' }}>What stays free?</h2>
          <p style={{ margin: 0, fontSize: 14, color: '#4b5563', lineHeight: 1.65 }}>Build and edit your CV without signing up. Free access includes the Modern and Minimal templates, three colours, and PDF export with a small MakeMyResume watermark. Upgrade only if you need the remaining templates, AI tools, CV import, more colours, or a watermark-free PDF.</p>
        </div>

        {/* FAQ */}
        <div style={{ maxWidth: 700, margin: '40px auto 0' }}>
          <h2 style={{ textAlign: 'center', fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Frequently asked questions</h2>
          {[
            { q: 'Will I be charged again for the 7-Day Pass?', a: 'No. It is a single payment and access ends after 7 days. It does not renew.' },
            { q: 'Can I cancel a subscription?', a: 'Yes. Cancel a monthly or annual plan before its next renewal from your account. You keep access through the paid period.' },
            { q: 'What payment methods do you accept?', a: 'Credit and debit cards via Stripe. Additional payment methods may appear at checkout where available.' },
            { q: 'Is my data safe?', a: 'Yes. Your CV data stays in your browser. Only your subscription status is stored on our servers.' },
            { q: 'Do you offer refunds?', a: 'Yes — 7-day money-back guarantee for monthly/annual plans. Just email support@makemyresume.app.' },
            { q: 'How is the One-Time plan different?', a: 'It gives you 7 days of full access, then automatically ends with no charges. Great for a single job application.' }
          ].map((item, i) => (
            <details key={i} style={{ background: '#fff', borderRadius: 10, marginBottom: 8, padding: '14px 18px', border: '1px solid #e5e7eb' }}>
              <summary style={{ fontWeight: 600, cursor: 'pointer', fontSize: 14, color: '#111827' }}>{item.q}</summary>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>{item.a}</p>
            </details>
          ))}
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
